import React, { useState, useRef, useEffect, useCallback } from 'react';
import Popper, { Placement } from '@gr-monorepo/common/ui/Popper';
import styled from 'styled-components/macro';
import { ReactComponent as Icon } from './icon.svg';
import { Warning } from 'features/shared/ui/sharedStyled';
import Tooltip, { TooltipPlacement } from 'features/shared/Tooltip';

export interface ValueProps<IdType = number | string> {
  id: IdType;
  label: string;
}
const Text = styled.span`
  font-weight: 700;
  font-size: 30px;
  line-height: 40px;
`;

const Triangle = styled(Icon)<{ open: boolean }>`
  margin: 0px 12px 0 8px;
  transform: rotate(${(props) => (props.open ? '0' : '180deg')});
`;

const DateWrap = styled.div`
  display: inline-flex;
  align-items: center;

  ${Text}, ${Triangle} {
    cursor: pointer;
  }
`;

const List = styled.ul`
  background: #ffffff;
  border-radius: 10px;
  padding: 10px 0;
  filter: drop-shadow(0px 8px 18px rgba(57, 78, 127, 0.2));
`;

const Item = styled.li<{ selected?: boolean }>`
  cursor: pointer;
  padding: 4px 12px;
  ${(props) => props.selected && 'background-color:  rgba(9, 21, 38, 0.07);'}

  &:hover {
    background-color: #e3f4f9;
  }
`;

function YearSelector<IdType>(props: {
  values?: ValueProps<IdType>[];
  value?: ValueProps<IdType>;
  handleChange?: (value: ValueProps<IdType>) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const targetRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const handleClickOverlay = useCallback(
    (e: MouseEvent) => {
      if (listRef.current && e.target) {
        if (
          !(
            listRef.current === e.target ||
            targetRef.current === e.target ||
            Array.from(listRef.current!.querySelectorAll('*')).includes(e.target as any) ||
            Array.from(targetRef.current!.querySelectorAll('*')).includes(e.target as any)
          )
        )
          close();
      }
    },
    [close, listRef],
  );
  const handleClick = (value: ValueProps<IdType>) => {
    props.handleChange?.(value);
    close();
  };
  useEffect(() => {
    if (isOpen) {
      const body = document.querySelector('body');
      body!.addEventListener('click', handleClickOverlay);
      body!.addEventListener('mouseup', handleClickOverlay);
      body!.addEventListener('mousedown', handleClickOverlay);
      return () => {
        body!.removeEventListener('click', handleClickOverlay);
        body!.removeEventListener('mouseup', handleClickOverlay);
        body!.removeEventListener('mousedown', handleClickOverlay);
      };
    }
  }, [isOpen, handleClickOverlay]);

  return (
    <>
      <DateWrap ref={targetRef}>
        <Text
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        >
          {props.value?.label || ''}
        </Text>
        <Triangle
          open={isOpen}
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        />
        <Tooltip
          title="Нажмите на треугольник, чтобы посмотреть статистику за другие периоды"
          arrow
          placement={TooltipPlacement.BottomStart}
          width={384}
        >
          <Warning style={{ marginRight: -12 }} />
        </Tooltip>
      </DateWrap>
      <Popper
        open={Boolean(targetRef.current) && isOpen}
        targetRef={targetRef.current || undefined}
        placement={Placement.BottomStart}
        style={{ width: targetRef.current?.clientWidth }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [-12, 0],
            },
          },
        ]}
      >
        <div>
          <List ref={listRef}>
            {props.values?.map((el) => (
              <Item key={el.id + ''} onClick={() => handleClick(el)} selected={el.id === props.value?.id}>
                <Text>{el.label}</Text>
              </Item>
            ))}
          </List>
        </div>
      </Popper>
    </>
  );
}

export default YearSelector;
